import * as React from 'react';

import { MaybeWrap } from '@rematter/ui-web';
import { memoize } from 'micro-memoize';
import { match } from 'path-to-regexp';
import {
  BaseLocationHook,
  Redirect,
  Route,
  Router,
  Switch,
  useLocation,
} from 'wouter';

import { NavItem } from './createModuleNav';
import { ModuleDefinition, SubRoute } from './types';

type LayoutProps = {
  children: React.ReactNode;
  moduleNavigation?: NavItem;
};

export type RouteChildrenProps = {
  nav: NavItem | undefined;
  level: number;
};

const RouteChildren = (props: RouteChildrenProps) => {
  const [_location] = useLocation();
  const location = _location.replace(/\/$/, '');

  return (
    <Switch>
      {props.nav?.children.map((child) => {
        const withMatchingChild = hasMatchingChild(child, location);
        const routePath = withMatchingChild
          ? `${child.route.path}/*?`
          : child.route.path;

        return (
          <Route key={child.route.path} path={routePath}>
            {() => {
              const Component = child.route.component ?? React.Fragment;

              return (
                <Component>
                  <RouteChildren nav={child} level={props.level + 1} />
                </Component>
              );
            }}
          </Route>
        );
      })}
    </Switch>
  );
};

const useRedirectsByPath = (nav: NavItem | undefined) =>
  React.useMemo(() => {
    const firstChildrenMap = new Map<string, SubRoute>();

    const extractFirstChildrenRedirections = (menuItem?: NavItem) => {
      if (!menuItem?.route.component && menuItem?.children) {
        firstChildrenMap.set(menuItem.route.path, menuItem.children[0].route);
      }

      menuItem?.children?.forEach(extractFirstChildrenRedirections);
    };

    extractFirstChildrenRedirections(nav);

    return firstChildrenMap;
  }, [nav]);

const hasMatchingChild = memoize(
  (node: NavItem | undefined, location: string): boolean => {
    if (!node) return false;

    const matchResult = match(node.route.path)(location);

    return (
      !!matchResult ||
      !!node.children?.find((child) => hasMatchingChild(child, location))
    );
  },
);

const createModuleRouter =
  ({
    nav,
    layout,
    locationHook,
  }: {
    nav: NavItem;
    layout?: React.ComponentType<LayoutProps>;
    locationHook?: BaseLocationHook;
  }) =>
  () => {
    const useLocationHook = locationHook ?? useLocation;
    const [rawLocation] = useLocationHook();

    const location = rawLocation.replace(/\/$/, '');
    const redirectsByPath = useRedirectsByPath(nav);

    const { path: routePath, component: RouteComponent = React.Fragment } =
      nav.route;

    const redirectTo = redirectsByPath.get(location);
    const Layout = layout ?? React.Fragment;
    const layoutProps = layout ? { moduleNavigation: nav } : {};
    const routePathPattern = `${routePath}/*?`;

    return (
      <MaybeWrap
        condition={!!locationHook}
        wrapper={(children) => (
          <Router hook={useLocationHook}>{children}</Router>
        )}
      >
        {redirectTo ? (
          <Redirect to={redirectTo.path} replace />
        ) : (
          <Route key={routePath} path={routePathPattern}>
            <Layout {...layoutProps}>
              <RouteComponent>
                <RouteChildren nav={nav} level={1} />
              </RouteComponent>
            </Layout>
          </Route>
        )}
      </MaybeWrap>
    );
  };

export const createAppModule = ({
  routes,
  nav,
  layout,
  requiredEntitlement,
  locationHook,
  isExclusive,
}: {
  routes: { root: SubRoute } & Record<string, SubRoute>;
  nav: NavItem;
  layout?: React.ComponentType<LayoutProps>;
  requiredEntitlement?: string;
  locationHook?: BaseLocationHook;
  isExclusive?: boolean;
}): ModuleDefinition => {
  const Router = createModuleRouter({ nav, layout, locationHook });

  return {
    path: routes.root.path,
    label: routes.root.label ?? (() => ''),
    icon: routes.root.icon,
    Router,
    nav,
    requiredEntitlement: requiredEntitlement,
    isExclusive: isExclusive,
  };
};
